import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import useEmblaCarousel from 'embla-carousel-react';
import { Avatar, Text } from 'components';
import { Api } from 'lib';
import { REFERRAL_COLORS } from './theme';
import ReferralFeedSkeleton from './ReferralFeedSkeleton';
import ReferralCommentsSheet from './ReferralCommentsSheet';

// Same path as playaz4playaz-mobile/src/assets/icons/AddComment.js — used
// verbatim so the comment icon matches the mobile reel player exactly.
const ADD_COMMENT_PATH =
  'M20 2C20 0.9 19.1 0 18 0H2C0.9 0 0 0.9 0 2V14C0 15.1 0.9 16 2 16H16L20 20V2ZM18 15.17L16.83 14H2V2H18V15.17ZM11 3H9V7H5V9H9V13H11V9H15V7H11V3Z';

const AddCommentIcon = ({ size = 16, color = '#fff' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d={ADD_COMMENT_PATH} fill={color} />
  </svg>
);

// Colors below are lifted directly from playaz4playaz-mobile/src/lib/theme.ts
// and the FanCamLivePlayer/PlayerBottomBar/ReactionRow/ColorReactionChips/
// PointsPill components, so this stage matches the mobile reel viewer
// exactly rather than approximating it.
const BLACK_A40 = 'rgba(0, 0, 0, 0.4)';
const BLACK_A55 = 'rgba(0, 0, 0, 0.55)';
const WHITE_A20 = 'rgba(255, 255, 255, 0.2)';
const WHITE_A40 = 'rgba(255, 255, 255, 0.4)';
const WHITE_A70 = 'rgba(255, 255, 255, 0.7)';
const AVATAR_RING = 'rgba(93, 95, 239, 1)';
const POINTS_PILL_BORDER = 'rgba(239, 93, 168, 0.5)';

// Embla viewport — vertical axis, matching the mobile reel's vertical swipe
// (react-native-pager-view orientation="vertical"). https://www.embla-carousel.com/docs/examples/predefined
const STAGE_HEIGHT = 'calc(100vh - 170px)';

const Viewport = styled.div`
  position: relative;
  height: ${STAGE_HEIGHT};
  margin: 0 16px 16px;
  border-radius: 16px;
  overflow: hidden;
  background-color: #000;
  @media (min-width: 1024px) {
    height: min(calc(100vh - 138px), 780px);
    margin: 0;
  }
`;

const EmblaContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  touch-action: pan-x pinch-zoom;
`;

const EmblaSlide = styled.div`
  position: relative;
  flex: 0 0 100%;
  min-height: 0;
`;

const StageMedia = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${(props) => (props.$mode === 'cover' ? 'cover' : 'contain')};
  display: block;
`;

const TopBarScrim = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
`;

const TopBar = styled.div`
  position: absolute;
  top: 8px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ViewCountGroup = styled.div`
  display: flex;
  align-items: center;
  background-color: ${BLACK_A40};
  padding: 4px 12px;
  border-radius: 999px;
  color: #fff;
  font-size: 12px;
`;

const NavColumn = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 5;
`;

const NavButton = styled.button`
  width: 35px;
  height: 35px;
  border-radius: 22px;
  border: none;
  background-color: ${BLACK_A40};
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const BottomScrim = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 40%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0), ${BLACK_A55} 60%, rgba(0, 0, 0, 0.7));
  pointer-events: none;
`;

const BottomContent = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 16px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const UserContainer = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  min-width: 0;
`;

const AvatarWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 22px;
  border: 2px solid ${AVATAR_RING};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const PointsPill = styled.div`
  background-color: ${BLACK_A40};
  border: 1px solid ${POINTS_PILL_BORDER};
  border-radius: 999px;
  padding: 4px 10px;
`;

const HeaderRightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  border: none;
  background-color: ${BLACK_A40};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
`;

const CaptionBox = styled.div`
  margin-top: 10px;
  padding: 12px;
  border-radius: 12px;
  background-color: ${BLACK_A40};
  border: 1px solid ${WHITE_A20};
`;

const ReactionRowWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  margin-top: 10px;
  padding-bottom: 2px;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ReactionChip = styled.button`
  flex: none;
  display: flex;
  align-items: center;
  background-color: ${BLACK_A40};
  border: none;
  border-radius: 999px;
  padding: 6px 8px;
  cursor: pointer;
`;

const ColorDot = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 7px;
  background-color: ${(props) => props.$hex};
  border: ${(props) => (props.$active ? `2px solid #fff` : `1px solid ${WHITE_A40}`)};
  display: inline-block;
`;

const EMOJI_REACTIONS = [
  { key: 'like', emoji: '👍' },
  { key: 'love', emoji: '❤️' },
  { key: 'care', emoji: '🤗' },
  { key: 'haha', emoji: '😂' },
  { key: 'wow', emoji: '😮' },
  { key: 'sad', emoji: '😢' },
  { key: 'angry', emoji: '😡' }
];

// Same fixed static palette as ColorReactionChips.tsx.
const COLOR_REACTIONS = [
  { key: 'blue', hex: '#1877F2' },
  { key: 'red', hex: '#F02849' },
  { key: 'green', hex: '#31C48D' },
  { key: 'yellow', hex: '#FFC700' },
  { key: 'white', hex: '#FFFFFF' },
  { key: 'black', hex: '#000000' }
];

function formatCompact(n) {
  if (!n) return 0;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}m`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n;
}

function FancamSlide({
  item,
  currentUser,
  isActive,
  onReact,
  onColorReact,
  onAvatarPress,
  onComments
}) {
  const videoRef = useRef(null);
  const isVideo = item.media?.type === 'video';

  useEffect(() => {
    if (!isVideo || !videoRef.current) return;
    if (isActive) {
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [isActive, isVideo]);

  const currentReaction = useMemo(() => {
    if (!item?.reactions || !currentUser?._id) return null;
    const found = item.reactions.find(
      (r) => r.user === currentUser._id || r.user?._id === currentUser._id
    );
    return found?.reaction ?? null;
  }, [item, currentUser?._id]);

  const currentColorReaction = useMemo(() => {
    if (!item?.colorReactions || !currentUser?._id) return null;
    const found = item.colorReactions.find(
      (r) => r.user === currentUser._id || r.user?._id === currentUser._id
    );
    return found?.color ?? null;
  }, [item, currentUser?._id]);

  const ownerName = item.owner?.name || item.owner?.username || 'Unknown';
  const mediaMode =
    item.media?.aspectRatio != null && item.media.aspectRatio < 0.9 ? 'cover' : 'contain';

  return (
    <EmblaSlide>
      <StageMedia>
        {isVideo ? (
          <CardImage
            as="video"
            ref={videoRef}
            src={item.media?.url}
            poster={item.media?.thumbnail}
            $mode={mediaMode}
            muted
            loop
            playsInline
          />
        ) : (
          <CardImage src={item.media?.thumbnail || item.media?.url} alt="" $mode={mediaMode} />
        )}
      </StageMedia>
      <TopBarScrim />
      <TopBar>
        <ViewCountGroup>👁 {formatCompact(item.viewCount)}</ViewCountGroup>
      </TopBar>

      <BottomScrim />
      <BottomContent>
        <HeaderRow>
          <HeaderLeftGroup>
            <UserContainer onClick={() => onAvatarPress(item.owner?.username)}>
              <AvatarWrapper>
                <Avatar user={item.owner} small />
              </AvatarWrapper>
              <Text size="13px" style={{ fontWeight: 900 }} col="#fff" marg="0 0 0 6px">
                {ownerName}
              </Text>
            </UserContainer>
            {item.owner?.points != null && (
              <PointsPill>
                <Text size="12px" style={{ fontWeight: 900 }} col="#fff">
                  {formatCompact(item.owner.points)} pts
                </Text>
              </PointsPill>
            )}
          </HeaderLeftGroup>

          <HeaderRightGroup>
            <IconButton onClick={() => onComments(item._id)} aria-label="Comments">
              <AddCommentIcon size={16} />
            </IconButton>
          </HeaderRightGroup>
        </HeaderRow>

        {item.caption && (
          <CaptionBox>
            <Text size="13px" col="#fff">
              {item.caption}
            </Text>
          </CaptionBox>
        )}

        <ReactionRowWrapper>
          {EMOJI_REACTIONS.map((r) => (
            <ReactionChip key={r.key} onClick={() => onReact(item, r.key, currentReaction)}>
              <span style={{ fontSize: 16 }}>{r.emoji}</span>
              <Text
                size="12px"
                col={currentReaction === r.key ? '#fff' : WHITE_A70}
                marg="0 0 0 3px"
              >
                {formatCompact(item.reactionCounts?.[r.key] || 0)}
              </Text>
            </ReactionChip>
          ))}
          {COLOR_REACTIONS.map((c) => (
            <ReactionChip
              key={c.key}
              onClick={() => onColorReact(item, c.key, currentColorReaction)}
            >
              <ColorDot $hex={c.hex} $active={currentColorReaction === c.key} />
              <Text
                size="12px"
                col={currentColorReaction === c.key ? '#fff' : WHITE_A70}
                marg="0 0 0 3px"
              >
                {formatCompact(item.colorReactionCounts?.[c.key] || 0)}
              </Text>
            </ReactionChip>
          ))}
        </ReactionRowWrapper>
      </BottomContent>
    </EmblaSlide>
  );
}

const PAGE_SIZE = 10;

/**
 * The team's FanCam Challenge feed the referred friend scrolls. A "View My FanCam Post" referral
 * passes `pinnedFancamId`: that post leads the feed (the team's pages follow, without it), every
 * view is reported against its referral, and the saved resume position is ignored so the friend
 * always lands on it.
 */
const ReferralFeed = ({ teamId, senderId, shareToken, pinnedFancamId, onViewTracked }) => {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user);
  const [fancams, setFancams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCommentsFor, setShowCommentsFor] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const trackedIndexRef = useRef(-1);
  // Team posts fetched so far — the paging offset. Not `fancams.length`: the pinned post is not
  // one of the team pages, and it is dropped from them where they repeat it.
  const teamOffsetRef = useRef(0);
  const fancamsRef = useRef(fancams);
  useEffect(() => {
    fancamsRef.current = fancams;
  }, [fancams]);

  // hasMore/isLoadingMore are mirrored into refs (same reason fancamsRef
  // exists above): onSelect is a stable callback attached once to the
  // embla 'select' event, so reading component state directly inside it
  // would see stale values from whenever it was first attached.
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const hasMoreRef = useRef(hasMore);
  const isLoadingMoreRef = useRef(isLoadingMore);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  const [emblaRef, emblaApi] = useEmblaCarousel({ axis: 'y', loop: false });
  const didRestorePositionRef = useRef(false);

  useEffect(() => {
    if (!emblaApi) return;
    const raf = requestAnimationFrame(() => {
      emblaApi.reInit();
      if (!didRestorePositionRef.current && fancams.length > 0) {
        didRestorePositionRef.current = true;
        if (pinnedFancamId) return;
        let savedFancamId = null;
        try {
          savedFancamId = teamId ? localStorage.getItem(`referralFeedPosition:${teamId}`) : null;
        } catch {
          savedFancamId = null;
        }
        const index = savedFancamId ? fancams.findIndex((f) => f._id === savedFancamId) : -1;
        if (index > 0) emblaApi.scrollTo(index, true);
      }
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, fancams.length, teamId, pinnedFancamId]);

  useEffect(() => {
    if (!teamId) return;
    setIsLoading(true);
    setHasMore(true);

    Promise.all([
      Api.get(`/team/fancam/challenge/get/${teamId}?offset=0&limit=${PAGE_SIZE}`),
      // Silent: a pinned post that has since gone private or been removed is not worth a toast —
      // the friend still gets the team's feed.
      pinnedFancamId
        ? Api.get(`/team/fancam/getById/${pinnedFancamId}`, null, { silent: true })
        : null
    ]).then(([res, pinnedRes]) => {
      const list = Array.isArray(res) ? res : (res?.fancams ?? []);
      // getById answers with the bare fancam (the backend unwraps its `{ fancam }`); read the
      // wrapped form too, as `getFanCamById` on mobile does. A silent failure is `{ err, status }`.
      const pinned = pinnedRes?.fancam ?? (pinnedRes?._id ? pinnedRes : null);
      teamOffsetRef.current = list.length;
      setFancams(pinned ? [pinned, ...list.filter((fc) => fc._id !== pinned._id)] : list);
      setHasMore(list.length >= PAGE_SIZE);
      setIsLoading(false);
    });
  }, [teamId, pinnedFancamId]);

  async function fetchMore() {
    if (!teamId || hasMoreRef.current === false || isLoadingMoreRef.current) return;
    setIsLoadingMore(true);
    const offset = teamOffsetRef.current;
    const res = await Api.get(
      `/team/fancam/challenge/get/${teamId}?offset=${offset}&limit=${PAGE_SIZE}`
    );
    const list = Array.isArray(res) ? res : (res?.fancams ?? []);
    teamOffsetRef.current += list.length;
    setFancams((prev) => [...prev, ...list.filter((fc) => fc._id !== pinnedFancamId)]);
    setHasMore(list.length >= PAGE_SIZE);
    setIsLoadingMore(false);
  }

  const onSelect = useCallback(
    (api) => {
      const index = api.selectedScrollSnap();
      setActiveIndex(index);
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
      const item = fancamsRef.current[index];
      if (teamId && item) {
        try {
          localStorage.setItem(`referralFeedPosition:${teamId}`, item._id);
        } catch {
          // Resume position is a nice-to-have — never worth crashing the feed
          // over (private-mode/quota can throw here).
        }
      }

      if (fancamsRef.current.length > 0 && index >= fancamsRef.current.length - 3) {
        fetchMore();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamId]
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    const item = fancamsRef.current[activeIndex];
    if (!item || trackedIndexRef.current === activeIndex) return;
    trackedIndexRef.current = activeIndex;

    applyOverride(item._id, (fc) => ({ viewCount: (fc.viewCount || 0) + 1 }));

    Api.post(
      '/referral/track-view',
      {
        fancamId: item._id,
        referrerId: senderId,
        // Each team-wide link has its own referral row; its token is how the backend finds it.
        ...(pinnedFancamId ? { referralFancamId: pinnedFancamId } : { shareToken })
      },
      null,
      { silent: true }
    ).then((res) => {
      if (typeof res?.momentsViewed === 'number') onViewTracked(res);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, onViewTracked]);

  function applyOverride(fancamId, updater) {
    setFancams((prev) => prev.map((fc) => (fc._id === fancamId ? { ...fc, ...updater(fc) } : fc)));
  }

  function handleReact(item, reactionKey, currentReaction) {
    const nextReaction = currentReaction === reactionKey ? 'unlike' : reactionKey;
    applyOverride(item._id, (fc) => {
      const counts = { ...(fc.reactionCounts || {}) };
      if (currentReaction)
        counts[currentReaction] = Math.max(0, (counts[currentReaction] || 1) - 1);
      if (nextReaction !== 'unlike') counts[nextReaction] = (counts[nextReaction] || 0) + 1;
      const reactions = (fc.reactions || []).filter(
        (r) => r.user !== currentUser?._id && r.user?._id !== currentUser?._id
      );
      if (nextReaction !== 'unlike')
        reactions.push({ user: currentUser._id, reaction: nextReaction });
      return { reactionCounts: counts, reactions };
    });
    Api.post('/users/react', { entityId: item._id, entityType: 'fancam', reaction: nextReaction });
  }

  function handleColorReact(item, colorKey, currentColorReaction) {
    const nextColor = currentColorReaction === colorKey ? null : colorKey;
    applyOverride(item._id, (fc) => {
      const counts = { ...(fc.colorReactionCounts || {}) };
      if (currentColorReaction)
        counts[currentColorReaction] = Math.max(0, (counts[currentColorReaction] || 1) - 1);
      if (nextColor) counts[nextColor] = (counts[nextColor] || 0) + 1;
      const colorReactions = (fc.colorReactions || []).filter(
        (r) => r.user !== currentUser?._id && r.user?._id !== currentUser?._id
      );
      if (nextColor) colorReactions.push({ user: currentUser._id, color: nextColor });
      return { colorReactionCounts: counts, colorReactions };
    });
    Api.post('/team/fancam/colorReact', { fancamId: item._id, color: nextColor || colorKey });
  }

  if (isLoading) return <ReferralFeedSkeleton />;

  if (!fancams.length) {
    return (
      <Viewport>
        <BottomContent>
          <Text b2 col={REFERRAL_COLORS.mutedText}>
            No FanCam moments available for this team yet.
          </Text>
        </BottomContent>
      </Viewport>
    );
  }

  return (
    <Viewport ref={emblaRef}>
      <EmblaContainer>
        {fancams.map((item, index) => (
          <FancamSlide
            key={item._id}
            item={item}
            currentUser={currentUser}
            isActive={index === activeIndex}
            onReact={handleReact}
            onColorReact={handleColorReact}
            onAvatarPress={(username) => navigate(`/profile/${username}`)}
            onComments={(fancamId) => setShowCommentsFor(fancamId)}
          />
        ))}
      </EmblaContainer>

      <NavColumn>
        <NavButton
          disabled={!canScrollPrev}
          onClick={() => emblaApi?.scrollPrev()}
          aria-label="Previous moment"
        >
          ▲
        </NavButton>
        <NavButton
          disabled={!canScrollNext}
          onClick={() => emblaApi?.scrollNext()}
          aria-label="Next moment"
        >
          ▼
        </NavButton>
      </NavColumn>

      {showCommentsFor && (
        <ReferralCommentsSheet
          fancamId={showCommentsFor}
          onClose={() => setShowCommentsFor(null)}
        />
      )}
    </Viewport>
  );
};

export default ReferralFeed;
